import { generateUUID, isPlainObject, toKebabCase } from "./functions";

/**
 * PlainObject를 CSS 셀렉터 문자열로 변환합니다.
 * 
 * @param {Object} obj - 셀렉터로 변환할 객체
 *   - id: string|null|undefined - id 셀렉터 (#id)
 *   - name: string|null|undefined - name 속성 셀렉터 ([name="value"])
 *   - form: string|null|undefined - form 속성 셀렉터 ([form="value"])
 *   - class: string|null|undefined - class 셀렉터 (.class1.class2)
 *   - "class~": string|null|undefined - class 부분 매칭 셀렉터 ([class~="value"])
 *   - data: Object|null|undefined - data 속성 셀렉터 ([data-key="value"])
 *   - "data~": Object|null|undefined - data 부분 매칭 셀렉터 ([data-key~="value"])
 * 
 * @returns {string|null} CSS 셀렉터 문자열, 유효한 셀렉터가 없으면 null
 * 
 * @example
 * // id 셀렉터
 * querySelectorStringByObject({ id: "myId" }) // "#myId"
 * querySelectorStringByObject({ id: null }) // "[id]" (id 속성이 있는 요소)
 * 
 * // name 셀렉터
 * querySelectorStringByObject({ name: "email" }) // '[name="email"]'
 * querySelectorStringByObject({ name: null }) // '[name]'
 * 
 * // form 셀렉터
 * querySelectorStringByObject({ form: "myForm" }) // '[form="myForm"]'
 * 
 * // class 셀렉터
 * querySelectorStringByObject({ class: "btn primary" }) // ".btn.primary"
 * querySelectorStringByObject({ "class~": "btn" }) // '[class~="btn"]'
 * 
 * // data 셀렉터
 * querySelectorStringByObject({ data: { controller: "modal", action: "click" } }) 
 * // '[data-controller="modal"][data-action="click"]'
 * 
 * querySelectorStringByObject({ "data~": { tags: "urgent" } }) // '[data-tags~="urgent"]'
 * querySelectorStringByObject({ data: { active: null } }) // '[data-active]'
 */

function querySelectorStringByObject(obj) {
  const parts = [];

  // id 처리
  if (obj.hasOwnProperty('id')) {
    const idValue = obj.id;
    if (idValue === null) {
      parts.push('[id]');
    } else if (idValue !== undefined && typeof idValue === 'string' && idValue.length > 0) {
      parts.push(`#${idValue}`);
    }
  }

  // name 처리
  if (obj.hasOwnProperty('name')) {
    const nameValue = obj.name;
    if (nameValue === null) {
      parts.push('[name]');
    } else if (nameValue !== undefined && typeof nameValue === 'string' && nameValue.length > 0) {
      parts.push(`[name="${nameValue}"]`);
    }
  }

  // form 처리
  if (obj.hasOwnProperty('form')) {
    const formValue = obj.form;
    if (formValue === null) {
      parts.push('[form]');
    } else if (formValue !== undefined && typeof formValue === 'string' && formValue.length > 0) {
      parts.push(`[form="${formValue}"]`);
    }
  }

  // class 처리
  if (obj.hasOwnProperty('class')) {
    const classValue = obj.class;
    if (classValue === null) {
      parts.push('[class]');
    } else if (classValue !== undefined && typeof classValue === 'string' && classValue.length > 0) {
      const classes = classValue.split(/\s+/).filter(c => c.length > 0);
      classes.forEach(c => parts.push(`.${c}`));
    }
  }

  // class~ 처리 (부분 매칭)
  if (obj.hasOwnProperty('class~')) {
    const classValue = obj['class~'];
    if (classValue === null) {
      parts.push('[class]');
    } else if (classValue !== undefined && typeof classValue === 'string' && classValue.length > 0) {
      const classes = classValue.split(/\s+/).filter(c => c.length > 0);
      classes.forEach(c => parts.push(`[class~="${c}"]`));
    }
  }

  // data 처리
  if (obj.hasOwnProperty('data')) {
    const dataValue = obj.data;
    if (dataValue === null) {
      parts.push('[data]');
    } else if (dataValue !== undefined && isPlainObject(dataValue)) {
      for (const [key, value] of Object.entries(dataValue)) {
        const dataKey = toKebabCase(key);
        if (value === null) {
          parts.push(`[data-${dataKey}]`);
        } else if (value !== undefined) {
          parts.push(`[data-${dataKey}="${value}"]`);
        }
      }
    }
  }

  // data~ 처리 (부분 매칭)
  if (obj.hasOwnProperty('data~')) {
    const dataValue = obj['data~'];
    if (dataValue === null) {
      parts.push('[data]');
    } else if (dataValue !== undefined && isPlainObject(dataValue)) {
      for (const [key, value] of Object.entries(dataValue)) {
        const dataKey = toKebabCase(key);
        if (value === null) {
          parts.push(`[data-${dataKey}]`);
        } else if (value !== undefined) {
          parts.push(`[data-${dataKey}~="${value}"]`);
        }
      }
    }
  }

  if (parts.length === 0) {
    console.warn("querySelectorStringByObject: 유효한 셀렉터가 생성되지 않았습니다.", obj);
    return null;
  }

  return parts.join('');
}

/**
 * 주어진 선택자(selector)에 해당하는 DOM 요소를 선택합니다.
 * 배열 형태의 선택자를 제공하면 계층적으로 요소를 선택합니다.
 * 
 * @param {HTMLElement|Array|string|Object} selector - DOM 요소를 선택하기 위한 선택자
 *   - HTMLElement: 해당 요소를 그대로 반환
 *   - string: document.querySelector를 사용하여 해당 선택자에 맞는 첫 번째 요소를 선택
 *   - PlainObject: id, class, data 속성을 CSS 셀렉터로 변환하여 요소 선택
 *     - { id: "myId" } → "#myId"
 *     - { id: null } → "[id]" (id 속성이 있는 요소)
 *     - { class: "btn primary" } → ".btn.primary"
 *     - { "class~": "btn" } → '[class~="btn"]' (부분 매칭)
 *     - { data: { controller: "modal" } } → '[data-controller="modal"]'
 *     - { data: { active: null } } → '[data-active]' (속성 존재 확인)
 *     - { "data~": { tags: "urgent" } } → '[data-tags~="urgent"]' (부분 매칭)
 *   - Array: 배열의 각 항목을 순차적으로 적용하여 계층적으로 요소를 선택
 *     - 배열의 첫 요소가 HTMLElement/document/PlainObject면 시작 컨텍스트로 사용
 *     - 중간 요소가 HTMLElement면 해당 요소가 컨텍스트가 됨
 *     - 마지막 요소가 HTMLElement면 컨텍스트 내에 있는지 확인 후 반환
 * @param {Function} [callback] - 선택된 요소에 적용할 콜백 함수
 * 
 * @returns {HTMLElement|null} 선택된 DOM 요소 또는 콜백 함수 적용 결과
 * 
 * @example
 * selectElement({ id: "myForm", class: "active" })
 * selectElement([{ data: { controller: "modal" } }, ".content"])
 */
export function selectElement(selector, callback) {
  let foundNode = null;

  // HTMLElement 처리
  if (selector instanceof HTMLElement) {
    foundNode = selector;
    // 문자열 선택자 처리
  } else if (typeof selector === 'string') {
    foundNode = document.querySelector(selector);
    // PlainObject 처리 (id, class, data를 셀렉터로 변환)
  } else if (isPlainObject(selector)) {
    const selectorString = querySelectorStringByObject(selector);
    if (selectorString) {
      foundNode = document.querySelector(selectorString);
    } else {
      return null;
    }
    // 배열 형태의 선택자 처리
  } else if (Array.isArray(selector)) {
    // 시작 컨텍스트 설정
    let currentContext = document;

    // 첫 번째 항목이 HTMLElement 또는 document인 경우 시작 컨텍스트로 설정
    if (selector.length > 0) {
      const firstItem = selector[0];
      if (firstItem instanceof HTMLElement || firstItem === document) {
        currentContext = firstItem;
        selector = selector.slice(1); // 첫 번째 항목 제거
      } else if (isPlainObject(firstItem)) {
        const selectorString = querySelectorStringByObject(firstItem);
        if (selectorString) {
          const foundContext = document.querySelector(selectorString);
          if (foundContext) {
            currentContext = foundContext;
          } else {
            return null;
          }
        } else {
          return null;
        }
        selector = selector.slice(1);
      }
    }

    // 배열의 각 선택자를 순차적으로 적용
    for (let i = 0; i < selector.length; i++) {
      const currentSelector = selector[i];
      const isLastItem = i === selector.length - 1;

      if (currentSelector instanceof HTMLElement) {
        if (isLastItem) {
          // 마지막 항목이 HTMLElement인 경우, 현재 컨텍스트 내에 있는지 확인
          if (currentContext.contains(currentSelector)) {
            currentContext = currentSelector;
          } else {
            return null; // 컨텍스트 내에 없으면 null 반환
          }
        } else {
          // 중간 항목이 HTMLElement인 경우, 컨텍스트 변경
          currentContext = currentSelector;
        }
      } else if (typeof currentSelector === 'string') {
        const nextNode = currentContext.querySelector(currentSelector);
        if (!nextNode) return null; // 중간에 요소를 찾지 못하면 null 반환
        currentContext = nextNode;
      } else if (isPlainObject(currentSelector)) {
        const selectorString = querySelectorStringByObject(currentSelector);
        if (selectorString) {
          const nextNode = currentContext.querySelector(selectorString);
          if (!nextNode) return null;
          currentContext = nextNode;
        } else {
          return null;
        }
      } else {
        console.warn("selectElement: 지원하지 않는 선택자 타입입니다.", currentSelector);
        return null;
      }
    }

    foundNode = currentContext;
  } else if (selector === document) {
    foundNode = document.querySelector('html');
  } else {
    console.warn("selectElement: 지원하지 않는 선택자 타입입니다.", selector);
    return null;
  }

  if (typeof callback === "function") {
    if (foundNode) {
      return callback(foundNode);
    } else {
      return null;
    }
  } else {
    return foundNode;
  }
}

/**
 * 주어진 선택자(selector)에 해당하는 DOM 요소들을 선택하고 선택적으로 콜백 함수를 각 요소에 적용합니다.
 * 배열 형태의 선택자를 제공하면 계층적으로 요소를 선택합니다.
 * 
 * @param {HTMLElement|Array|string|Object} selector - DOM 요소를 선택하기 위한 선택자
 *   - HTMLElement: 해당 요소를 배열로 감싸서 반환
 *   - string: document.querySelectorAll을 사용하여 해당 선택자에 맞는 모든 요소를 선택
 *   - PlainObject: id, class, data 속성을 CSS 셀렉터로 변환하여 요소들 선택
 *     - { id: "myId" } → "#myId"
 *     - { id: null } → "[id]" (id 속성이 있는 요소들)
 *     - { class: "btn primary" } → ".btn.primary"
 *     - { "class~": "btn" } → '[class~="btn"]' (부분 매칭)
 *     - { data: { controller: "modal" } } → '[data-controller="modal"]'
 *     - { data: { active: null } } → '[data-active]' (속성 존재 확인)
 *     - { "data~": { tags: "urgent" } } → '[data-tags~="urgent"]' (부분 매칭)
 *   - Array: 배열의 각 항목을 순차적으로 적용하여 계층적으로 요소를 선택
 *     - 배열의 첫 요소가 HTMLElement/document/PlainObject면 시작 컨텍스트로 사용
 *     - 중간 요소가 HTMLElement면 해당 요소가 컨텍스트가 됨
 *     - 마지막 요소가 HTMLElement면 컨텍스트 내에 있는지 확인 후 반환
 * @param {Function} [callback] - 각 선택된 요소에 적용할 콜백 함수
 * 
 * @returns {Array} 선택된 DOM 요소들의 배열 또는 콜백 함수 적용 결과의 배열
 * 
 * @example
 * selectElements({ class: "item" })
 * selectElements([{ data: { controller: "list" } }, { class: "item" }])
 */
export function selectElements(selector, callback) {
  let foundNodes = [];

  // 배열 형태의 선택자 처리
  if (Array.isArray(selector)) {
    if (selector.length > 0) {
      // 시작 컨텍스트 설정
      let contexts = [];
      let startIndex = 0;

      // 첫 번째 항목이 HTMLElement 또는 document인 경우 시작 컨텍스트로 설정
      const firstItem = selector[0];
      if (firstItem instanceof HTMLElement) {
        contexts = [firstItem];
        startIndex = 1;
      } else if (firstItem === document) {
        contexts = [document];
        startIndex = 1;
      } else if (typeof firstItem === 'string') {
        try {
          contexts = Array.from(document.querySelectorAll(firstItem));
        } catch (error) {
          console.error(error);
          contexts = [];
        }
        startIndex = 1;
      } else if (isPlainObject(firstItem)) {
        const selectorString = querySelectorStringByObject(firstItem);
        if (selectorString) {
          try {
            contexts = Array.from(document.querySelectorAll(selectorString));
          } catch (error) {
            console.error(error);
            contexts = [];
          }
        } else {
          contexts = [];
        }
        startIndex = 1;
      } else {
        console.warn("selectElements: 지원하지 않는 선택자 타입입니다.", firstItem);
        contexts = [document]; // 기본값으로 document 사용
      }

      // 나머지 선택자를 순차적으로 적용
      for (let i = startIndex; i < selector.length; i++) {
        const currentSelector = selector[i];
        const newContexts = [];
        const isLastItem = i === selector.length - 1;

        // 현재 모든 컨텍스트에 대해 선택자 적용
        for (const context of contexts) {
          if (currentSelector instanceof HTMLElement) {
            if (isLastItem) {
              // 마지막 항목이 HTMLElement인 경우, 현재 컨텍스트 내에 있는지 확인
              if (context.contains(currentSelector)) {
                newContexts.push(currentSelector);
              }
            } else {
              // 중간 항목이 HTMLElement인 경우, 컨텍스트 변경
              newContexts.push(currentSelector);
            }
          } else if (typeof currentSelector === 'string') {
            try {
              const selectedNodes = Array.from(context.querySelectorAll(currentSelector));
              newContexts.push(...selectedNodes);
            } catch (error) {
              console.error(error);
            }
          } else if (isPlainObject(currentSelector)) {
            const selectorString = querySelectorStringByObject(currentSelector);
            if (selectorString) {
              try {
                const selectedNodes = Array.from(context.querySelectorAll(selectorString));
                newContexts.push(...selectedNodes);
              } catch (error) {
                console.error(error);
              }
            }
            // selectorString이 null이면 빈 결과 (경고는 이미 querySelectorStringByObject에서 출력됨)
          } else {
            console.warn("selectElements: 지원하지 않는 선택자 타입입니다.", currentSelector);
          }
        }

        // 새 컨텍스트로 업데이트
        contexts = newContexts;
      }

      // 최종 컨텍스트가 결과
      foundNodes = contexts;
    }
  }
  // HTMLElement 처리
  else if (selector instanceof HTMLElement) {
    foundNodes = [selector];
  }
  // PlainObject 처리
  else if (isPlainObject(selector)) {
    const selectorString = querySelectorStringByObject(selector);
    if (selectorString) {
      try {
        foundNodes = Array.from(document.querySelectorAll(selectorString));
      } catch (error) {
        console.error(error);
        foundNodes = [];
      }
    } else {
      foundNodes = [];
    }
  }
  // document 처리
  else if (selector === document) {
    foundNodes = document.querySelector('html');
  }
  // 문자열 선택자 처리
  else if (typeof selector === 'string') {
    try {
      foundNodes = Array.from(document.querySelectorAll(selector));
    } catch (error) {
      console.error(error);
      foundNodes = [];
    }
  } else {
    console.warn("selectElements: 지원하지 않는 선택자 타입입니다.", { selector });
    foundNodes = [];
  }

  if (typeof callback === 'function') {
    return foundNodes.map(foundNode => {
      const callbackResult = callback(foundNode);
      return typeof callbackResult === 'undefined' ? foundNode : callbackResult;
    });
  } else {
    return foundNodes;
  }
}

export function selectParentElement(startSelector, matchSelector, callback, unlessFound = null) {
  let node = selectElement(startSelector);
  while (node && node.parentElement) {
    node = node.parentElement;
    let isMatch = false;
    if (matchSelector instanceof HTMLElement) {
      isMatch = node === matchSelector;
    } else if (typeof matchSelector === "string") {
      isMatch = node.matches(matchSelector);
    }
    if (isMatch) {
      if (typeof callback === "function") {
        return callback(node);
      } else {
        return node;
      }
    }
  }
  if (typeof unlessFound === "function") {
    return unlessFound();
  } else {
    return null;
  }
}

// dataset외의 다른 스코프에서 dataset을 흉내내는 DOMStringMap을 반환
/* 사용 예시: (본 예시에서 scope="staff"는 가이드 역할이지 기능적으로는 필요하진 않습니다)
<div scope="staff" staff-id="123" staff-first-name="John Doe" staff-is_deleted="false"></div>
const staffDataset = selectElement("[scope='staff']", el=>customDataset(el, "staff"))
staffDataset.id // "123"
staffDataset.firstName // "John Doe"
staffDataset.is_deleted // "false"

staffDataset.lastName = "Smith" // 새로운 속성 추가
// <div scope="staff" ...생략 staff-last-name="Smith"></div> 추가됨
*/
export function customDataset(selector, stringMapName = "custom") {
  const element = selectElement(selector);
  if (!element) {
    throw new Error(`Element not found for selector: ${selector}`);
  }

  const prefix = `${toKebabCase(stringMapName)}-`;

  // 케밥케이스를 카멜케이스로 변환 (예: "first-name" -> "firstName")
  function kebabToCamel(str) {
    return str.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  }

  // 카멜케이스를 케밥케이스로 변환 (예: "firstName" -> "first-name")
  function camelToKebab(str) {
    return str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
  }

  // 속성 이름을 HTML 속성명으로 변환 (예: "firstName" -> "staff-first-name")
  function propertyToAttribute(property) {
    return `${prefix}${camelToKebab(property)}`;
  }

  // HTML 속성명을 속성 이름으로 변환 (예: "staff-first-name" -> "firstName") 
  function attributeToProperty(attrName) {
    if (!attrName.startsWith(prefix)) return null;
    const suffix = attrName.slice(prefix.length);
    return kebabToCamel(suffix);
  }

  // 현재 요소의 모든 관련 속성 수집
  function getAllProperties() {
    const properties = {};
    for (const attr of element.attributes) {
      const property = attributeToProperty(attr.name);
      if (property !== null) {
        properties[property] = attr.value;
      }
    }
    return properties;
  }

  // DOMStringMap 스타일의 프록시 객체 생성
  return new Proxy({}, {
    get(target, property) {
      if (typeof property === 'string') {
        const attrName = propertyToAttribute(property);
        return element.getAttribute(attrName) || undefined;
      }
      return undefined;
    },

    set(target, property, value) {
      if (typeof property === 'string') {
        const attrName = propertyToAttribute(property);
        element.setAttribute(attrName, String(value));
        return true;
      }
      return false;
    },

    has(target, property) {
      if (typeof property === 'string') {
        const attrName = propertyToAttribute(property);
        return element.hasAttribute(attrName);
      }
      return false;
    },

    deleteProperty(target, property) {
      if (typeof property === 'string') {
        const attrName = propertyToAttribute(property);
        element.removeAttribute(attrName);
        return true;
      }
      return false;
    },

    ownKeys(target) {
      return Object.keys(getAllProperties());
    },

    getOwnPropertyDescriptor(target, property) {
      if (typeof property === 'string') {
        const attrName = propertyToAttribute(property);
        if (element.hasAttribute(attrName)) {
          return {
            enumerable: true,
            configurable: true,
            value: element.getAttribute(attrName)
          };
        }
      }
      return undefined;
    }
  });
}

export function dispatchElement(selector, eventName, detail = null, bubbles = true) {
  const target = selectElement(selector);
  if (target) {
    const event = new CustomEvent(eventName, { detail: detail, bubbles: bubbles });
    target.dispatchEvent(event);
    return target;
  } else {
    console.warn(`Element with selector "${selector}" not found.`);
  }
}

export function handleElement(selector, eventName, detail = null) {
  return dispatchElement(selector, eventName, detail, false);
}

/**
 * 주어진 요소 내에서 포커스 가능한 요소 중 현재 포커스된 요소의 인덱스를 반환합니다.
 *
 * @param {HTMLElement} element - 탐색할 부모 요소
 * @returns {number|null} 포커스된 요소의 인덱스 (0부터 시작), 포커스된 요소가 없으면 null
 */
export function getFocusInIndex(element) {
  const focusableElements = Array.from(element.querySelectorAll('a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'));
  for (let i = 0; i < focusableElements.length; i++) {
    if (focusableElements[i] === document.activeElement) {
      return i;
    }
  }
  return null;
}

/**
 * 주어진 요소 내의 포커스 가능한 요소 중 특정 인덱스에 해당하는 요소에 포커스를 설정합니다.
 *
 * @param {HTMLElement} element - 탐색할 부모 요소
 * @param {number} index - 포커스를 설정할 요소의 인덱스 (0부터 시작)
 * @returns {boolean} 포커스 설정 성공 여부 (성공: true, 실패: false)
 */
export function setFocusInIndex(element, index) {
  const focusableElements = Array.from(element.querySelectorAll('a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'));
  if (index >= 0 && index < focusableElements.length) {
    focusableElements[index].focus();
    return true;
  }
  return false;
}

// alpine 객체를 포함하여 element의 value 값을 가져옴
export function valueOfXNode(selector) {
  const el = selectElement(selector)
  if (el._x_model) {
    return el._x_model.get()
  }
  if (el._x_bindings && el._x_bindings.hasOwnProperty('value')) {
    return el._x_bindings.value
  }
  return el.value
}

function xValueSetDispatch(el, event) {
  switch (event) {
    case "change":
      el.dispatchEvent(new Event("change", { bubbles: true }))
      break
    case "input":
      el.dispatchEvent(new Event("input", { bubbles: true }))
      break
    case "blur":
      el.dispatchEvent(new Event("blur", { bubbles: true }))
      break
    case "focus":
      el.dispatchEvent(new Event("focus", { bubbles: true }))
      break
    case "focusin":
      el.dispatchEvent(new Event("focusin", { bubbles: true }))
      break
    case "focusout":
      el.dispatchEvent(new Event("focusout", { bubbles: true }))
      break
    default:
      console.warn("xValueSetDispatch 지원하지 않는 이벤트 타입입니다.", event)
      break
  }
}

export function setValueOfXNode(selector, value, option = null) {
  const el = selectElement(selector)
  const dispatchOption = option?.dispatch
  if (el._x_model) {
    el._x_model.set(value)
    dispatchOption && xValueSetDispatch(el, dispatchOption, { value })
    return
  }
  if (el._x_bindings && el._x_bindings.hasOwnProperty('value')) {
    console.warn("입력할 수 없는 요소에 값을 할당하려고 합니다.", el, value)
    return
  }
  dispatchOption && xValueSetDispatch(el, dispatchOption, { value })
  el.value = value
}


export function elementId(element, prefix = "elid_") {
  const selectedElement = selectElements(element)
  if (!selectedElement.id) {
    selectedElement.id = `${prefix}${generateUUID("xxxx_xxxx_xxxx")}`
  }
  return selectedElement.id
}

/*
  flowData = Record<string, any> | 
            (downflowElement: Element)=> Record<string, any> | 
            (downflowElement: Element)=> (downflowMatchResult: any) => Record<string, any>

  downflow("<selector>", (element)=>{
    const id = elementId(element)
    return {
      sectionName: "section",
      nextPage: ()=>{...},
    }
  })

  // 매칭
  downflow("<selector>", flowData, (downflowMatchingData)=>{
    return Boolean(downflowMatchingData) // 유사 true인 값을 반환할 경우 downflow의 flowData가 전달됨
  })
*/
const flowDebug = false

export function downflow(element, flowData, matcher = null, debug = false) {
  let { element: selectedElement, flowData: parsedFlowData, matcher: downflowMatcher } = downflowParameterParser({ element, flowData, matcher, debug })

  // downflow:collect 이벤트로 응답 가능한 downflow 수집
  selectedElement.addEventListener("downflow:collect", (event) => {
    // 이 downflow의 정보를 수집 목록에 추가
    event.detail.collectors.push({
      element: selectedElement,
      matcher: downflowMatcher,
      flowData: parsedFlowData,
      debug: debug
    })
  })
}

function downflowParameterParser({
  element,
  flowData,
  matcher = null,
  debug = false,
}) {
  const selectedElement = selectElement(element)
  if (!selectedElement) {
    throw new Error(`[downflow] Element not found for selector: ${element}`)
  }

  if (matcher === null) {
    matcher = (upflowPayload) => {
      if (Boolean(upflowPayload)) {
        if (debug) {
          console.log("[downflow] matching ", element, "from", { upflowPayload })
        }
        return true
      } else {
        return false
      }
    }
  } else if (typeof matcher == "string" && matcher.length > 0) {
    const matchString = matcher
    matcher = (childrenData) => childrenData == matchString
  } else if (Array.isArray(matcher) && matcher.length > 0) {
    const matchRules = []
    matcher.forEach((rule) => {
      if (typeof rule === "function") {
        matchRules.push(rule)
      } else if (typeof rule === "string" && rule.length > 0) {
        matchRules.push((childrenData) => {
          return childrenData == rule
        })
      } else {
        throw new Error("[downflow] matcher array contains invalid rule expected string | ()=>boolean ", rule)
      }
    })
    matcher = (childrenData) => {
      return Boolean(matchRules.length) && matchRules.some((rule) => rule(childrenData))
    }
  }

  if (typeof matcher !== "function") {
    throw new Error("[downflow] matcher is not a function")
  }

  let parsedFlowData = null
  if (typeof flowData === "function") {
    // plainObject or function
    parsedFlowData = flowData(selectedElement)
    if (!(typeof parsedFlowData === "function" || isPlainObject(parsedFlowData))) {
      throw new Error("[downflow] flowData function must return a function or plain object")
    }
  } else if (isPlainObject(flowData)) {
    parsedFlowData = flowData
  } else {
    throw new Error("[downflow] flowData is not a function or plain object")
  }

  return {
    element: selectedElement,
    flowData: parsedFlowData,
    matcher,
  }
}
/*
 
  upflow("<selector>", (flowData)=>{
    if(flowData.sectionName){
      flowData.nextPage()
    }
  })
 
  // 매칭
  upflow("<selector>", <callback>, (element)=>{
    return Boolean(element) // 유사 true인 값을 반환할 경우 upflow에서 매칭됨
  })

  // Promise 반환
  upflow("<selector>", <callback>, <matcher>)
    .then(() => { console.log("성공") })
    .catch((error) => { console.error("실패", error) })
*/
export function upflow(element, handleFlow, matcher = (downflowElement) => downflowElement, debug = false) {
  return new Promise((resolve, reject) => {
    try {
      const selectedElement = selectElement(element)
      if (!selectedElement) {
        throw new Error(`[upflow] Element not found for selector: ${element}`)
      }
      if (typeof handleFlow !== "function") {
        throw new Error("[upflow] handleFlow is not a function")
      }
      if (typeof matcher == "string" && matcher.length > 0) {
        const matchString = matcher
        matcher = () => matchString
      }
      if (typeof matcher !== "function") {
        throw new Error("[upflow] matcher is not string or function")
      }

      // 1단계: 모든 응답 가능한 downflow 수집
      const collectors = []
      const collectEvent = new CustomEvent("downflow:collect", {
        detail: { collectors },
        bubbles: true
      })
      selectedElement.dispatchEvent(collectEvent)

      if (debug || flowDebug) {
        console.log("[upflow] collected downflows", { count: collectors.length, collectors })
      }

      // 수집된 downflow가 없으면 reject
      if (collectors.length === 0) {
        const error = new Error("[upflow] no downflow collectors found")
        console.warn(error.message, {
          upflowElement: selectedElement,
          matcher: matcher.toString()
        })
        reject(error)
        return
      }

      // 2단계: 수집된 downflow들 중에서 매칭되는 것 찾기
      let matchedDownflow = null

      for (const collector of collectors) {
        try {
          // upflow의 matcher로 먼저 검사 (upflow가 원하는 downflow인지)
          const upflowMatchResult = matcher(collector.element)

          if (!Boolean(upflowMatchResult)) {
            if (collector.debug || debug || flowDebug) {
              console.log("[upflow] skip by upflow matcher", {
                downflowElement: collector.element,
                upflowMatchResult
              })
            }
            continue
          }

          // downflow의 matcher로 검사 (downflow가 이 요청을 받을 수 있는지)
          const downflowMatchResult = collector.matcher(upflowMatchResult)

          if (!Boolean(downflowMatchResult)) {
            if (collector.debug || debug || flowDebug) {
              console.log("[upflow] skip by downflow matcher", {
                downflowElement: collector.element,
                downflowMatchResult
              })
            }
            continue
          }

          // 매칭 성공!
          matchedDownflow = {
            collector,
            upflowMatchResult,
            downflowMatchResult
          }

          if (collector.debug || debug || flowDebug) {
            console.log("[upflow] matched!", {
              downflowElement: collector.element,
              upflowMatchResult,
              downflowMatchResult
            })
          }

          break // 첫 번째 매칭된 것만 사용
        } catch (matcherError) {
          // matcher 실행 중 에러 발생 시 reject
          console.error("[upflow] error in matcher", {
            error: matcherError,
            downflowElement: collector.element
          })
          reject(matcherError)
          return
        }
      }

      // 3단계: 매칭된 downflow 처리
      if (matchedDownflow) {
        try {
          const { collector, downflowMatchResult } = matchedDownflow

          // flowData 생성
          const sendToUpflowData = (
            typeof collector.flowData === "function" ?
              collector.flowData({ downflow: downflowMatchResult, downflowElement: collector.element }) :
              collector.flowData
          )

          // handleFlow 실행
          handleFlow(sendToUpflowData)

          // 성공적으로 완료
          resolve()
        } catch (handleFlowError) {
          // handleFlow 실행 중 에러 발생 시 reject
          console.error("[upflow] error in handleFlow", {
            error: handleFlowError,
            downflowElement: matchedDownflow.collector.element
          })
          reject(handleFlowError)
        }
      } else {
        // 매칭된 downflow가 없으면 reject
        const error = new Error("[upflow] no matching downflow found")
        console.warn(error.message, {
          upflowElement: selectedElement,
          collectedCount: collectors.length,
          matcher: matcher.toString()
        })
        reject(error)
      }
    } catch (error) {
      // 전체 실행 중 에러 발생 시 reject
      console.error("[upflow] unexpected error", { error })
      reject(error)
    }
  })
}


/*
  provideElement / consumeElement
  
  DOM 트리 기반의 Provider-Consumer 패턴 구현.
  상위 요소(Provider)가 하위 요소(Consumer)에게 context를 제공하고 통신하는 메커니즘.
  downflow/upflow의 개선판으로, 더 명확한 lifecycle과 context 관리를 제공합니다.

  사용 예시:
  ```javascript
  // Provider 설정
  const provider = provideElement('#listarea', 'list-scope', (provider) => {
    return {
      connected(consumer) {
        return { refresh: () => { ... } }
      },
      disconnected(consumer) { }
    }
  })

  // Consumer 설정
  const consumer = consumeElement(element, 'list-scope', (consumer) => {
    return {
      enter(context) { context.refresh() },
      exit(context, error) { },
      update(context) { }
    }
  })
  ```
*/

// Provider 인스턴스 저장소 (WeakMap으로 메모리 누수 방지)
const providerInstancesMap = new WeakMap()

// Provider 내부 메서드용 Symbol
// Symbol.for(전역 레지스트리)로 정의 — bun.build.js 가 splitting:false 로 여러 엔트리포인트를
// 빌드하며 domHelper 가 번들마다 중복 인라인되는데, 모듈-로컬 Symbol() 은 복제본마다 달라져
// 다른 번들에서 만든 provider 의 내부 메서드를 찾지 못한다(#89). 전역 심볼로 통일해 방지.
const SYM_PROVIDER_ADD_CONSUMER = Symbol.for('donghengin.domHelper.provider.addConsumer')
const SYM_PROVIDER_REMOVE_CONSUMER = Symbol.for('donghengin.domHelper.provider.removeConsumer')
const SYM_PROVIDER_MATCHERS = Symbol.for('donghengin.domHelper.provider.matchers')

/**
 * Provider 생성: 하위 Consumer에게 context를 제공하는 상위 요소
 * 
 * @param {string|Element|Array} selector - selectElement 호환 셀렉터
 * @param {string|Array|Function} matchers - consumer 연결 조건
 * @param {Function} [initializeCallback] - provider 초기화 및 lifecycle 정의
 * @returns {Object} provider 인스턴스
 */
export function provideElement(selector, matchers, initializeCallback = null) {
  const selectedElement = selectElement(selector)
  if (!selectedElement) {
    throw new Error(`[provideElement] Element not found for selector: ${selector}`)
  }

  // matchers 정규화
  const normalizedMatchers = normalizeProviderMatchers(matchers)

  // consumers 저장소 (WeakRef 사용하여 순환 참조 방지)
  const consumersSet = new Set()

  // provider 인스턴스 생성
  const provider = {
    element: selectedElement,
    [SYM_PROVIDER_MATCHERS]: normalizedMatchers,

    // 연결된 consumer 목록 (getter)
    get consumers() {
      const result = []
      const toRemove = []

      consumersSet.forEach((weakRef) => {
        const consumer = weakRef.deref()
        if (consumer && document.contains(consumer.element)) {
          result.push(consumer)
        } else {
          toRemove.push(weakRef)
        }
      })

      // 제거된 consumer 정리
      toRemove.forEach((ref) => consumersSet.delete(ref))

      return result
    },

    // 모든 consumer에게 broadcast
    broadcast(eventName, data = null, filter = null) {
      const consumers = this.consumers
      const results = []

      consumers.forEach((consumer) => {
        // 필터가 있으면 적용
        if (filter && typeof filter === 'function') {
          if (!filter(consumer)) return
        }

        // handleElement로 이벤트 dispatch
        const fullEventName = buildBroadcastEventName(consumer.assert, eventName)
        handleElement(consumer.element, fullEventName, data)
        results.push({ element: consumer.element, eventName: fullEventName })
      })

      return results
    },

    // provider 제거
    destroy() {
      // 모든 consumer 연결 해제
      const consumers = this.consumers
      consumers.forEach((consumer) => {
        if (consumer._internalDisconnect) {
          consumer._internalDisconnect(null)
        }
      })
      consumersSet.clear()

      // 이벤트 리스너 제거
      selectedElement.removeEventListener('provider:collect', collectHandler)

      // 저장소에서 제거
      const instances = providerInstancesMap.get(selectedElement)
      if (instances) {
        instances.delete(provider)
        if (instances.size === 0) {
          providerInstancesMap.delete(selectedElement)
        }
      }
    }
  }

  // 내부 메서드: consumer 추가
  provider[SYM_PROVIDER_ADD_CONSUMER] = function (consumer) {
    consumersSet.add(new WeakRef(consumer))
  }

  // 내부 메서드: consumer 제거
  provider[SYM_PROVIDER_REMOVE_CONSUMER] = function (consumer) {
    const toRemove = []
    consumersSet.forEach((weakRef) => {
      const ref = weakRef.deref()
      if (ref === consumer || !ref) {
        toRemove.push(weakRef)
      }
    })
    toRemove.forEach((ref) => consumersSet.delete(ref))
  }

  // lifecycle 콜백 저장
  let lifecycleCallbacks = null
  if (typeof initializeCallback === 'function') {
    lifecycleCallbacks = initializeCallback(provider)
  } else if (isPlainObject(initializeCallback)) {
    // plainObject → 자동으로 connected()에서 반환
    const contextObject = initializeCallback
    lifecycleCallbacks = {
      connected() { return contextObject }
    }
  }
  provider._lifecycleCallbacks = lifecycleCallbacks

  // provider:collect 이벤트 핸들러
  const collectHandler = (event) => {
    event.detail.collectors.push({
      element: selectedElement,
      matchers: normalizedMatchers,
      provider: provider,
      lifecycleCallbacks: lifecycleCallbacks
    })
  }

  selectedElement.addEventListener('provider:collect', collectHandler)

  // 인스턴스 저장 (한 element에 여러 provider 가능)
  if (!providerInstancesMap.has(selectedElement)) {
    providerInstancesMap.set(selectedElement, new Set())
  }
  providerInstancesMap.get(selectedElement).add(provider)

  return provider
}

/**
 * matchers 정규화
 */
function normalizeProviderMatchers(matchers) {
  if (typeof matchers === 'string') {
    return [{ type: 'name', value: matchers }]
  }

  if (Array.isArray(matchers)) {
    return matchers.map((m) => {
      if (typeof m === 'string') {
        return { type: 'name', value: m }
      }
      if (typeof m === 'function') {
        return { type: 'function', value: m }
      }
      throw new Error('[provideElement] Invalid matcher in array')
    })
  }

  if (typeof matchers === 'function') {
    return [{ type: 'function', value: matchers }]
  }

  throw new Error('[provideElement] matchers must be string, array, or function')
}

/**
 * broadcast 이벤트 이름 생성
 */
function buildBroadcastEventName(assert, eventName) {
  if (assert.consumer) {
    return `${assert.consumer}:listen:${assert.provider}:${eventName}`
  }
  return `listen:${assert.provider}:${eventName}`
}

/**
 * Consumer 생성: 상위 Provider에게 context를 받는 하위 요소
 * 
 * @param {string|Element|Array} selector - selectElement 호환 셀렉터
 * @param {string|Array|Object} assert - provider에게 전달되는 식별 정보
 * @param {Function} [initializeCallback] - consumer 초기화 및 lifecycle 정의
 * @returns {Object} consumer 인스턴스
 */
export function consumeElement(selector, assert, initializeCallback = null) {
  const selectedElement = selectElement(selector)
  if (!selectedElement) {
    throw new Error(`[consumeElement] Element not found for selector: ${selector}`)
  }

  // assert 정규화
  const normalizedAssert = normalizeConsumerAssert(assert)

  // 상태 관리
  let currentProvider = null
  let beforeProvider = null
  let currentContext = null
  let isConnected = false
  let lifecycleCallbacks = null
  const listenEntries = [] // listen으로 등록된 이벤트 핸들러 추적
  let stopPropagationFlag = false

  // consumer 인스턴스 생성
  const consumer = {
    element: selectedElement,

    get assert() {
      return normalizedAssert
    },

    get provider() {
      return currentProvider
    },

    get beforeProvider() {
      return beforeProvider || currentProvider
    },

    get connected() {
      return isConnected
    },

    // 현재 context 접근
    get context() {
      return currentContext
    },

    // 탐색 중단 (더 상위로 올라가지 않음)
    stopPropagation() {
      stopPropagationFlag = true
    },

    // 연결 시도
    connect() {
      if (isConnected) return this

      stopPropagationFlag = false

      // provider:collect 이벤트로 상위 provider 수집
      const collectors = []
      const collectEvent = new CustomEvent('provider:collect', {
        detail: { collectors },
        bubbles: true
      })
      selectedElement.dispatchEvent(collectEvent)

      // 매칭되는 provider 찾기
      for (const collector of collectors) {
        if (stopPropagationFlag) break

        const matchResult = matchProvider(collector, consumer, normalizedAssert)

        if (matchResult.matched) {
          // 연결 성공
          currentProvider = collector.provider
          beforeProvider = collector.provider
          isConnected = true

          // provider에 consumer 등록
          collector.provider[SYM_PROVIDER_ADD_CONSUMER](consumer)

          // provider의 connected 콜백 실행
          try {
            if (collector.lifecycleCallbacks && typeof collector.lifecycleCallbacks.connected === 'function') {
              currentContext = collector.lifecycleCallbacks.connected(consumer)
            }

            // consumer의 enter 콜백 실행
            if (lifecycleCallbacks && typeof lifecycleCallbacks.enter === 'function') {
              lifecycleCallbacks.enter(currentContext)
            }
          } catch (error) {
            console.error('[consumeElement] Error in connected/enter callback', error)
            this.disconnect(error)
          }

          break
        }

        if (stopPropagationFlag) break
      }

      return this
    },

    // 연결 해제
    disconnect(error = null) {
      if (!isConnected) return this

      this._internalDisconnect(error)

      return this
    },

    // 내부 연결 해제 (provider.destroy에서도 호출)
    _internalDisconnect(error) {
      if (!isConnected) return

      isConnected = false

      // listen으로 등록된 리스너 제거
      listenEntries.forEach(({ eventName, handler }) => {
        selectedElement.removeEventListener(eventName, handler)
      })
      listenEntries.length = 0

      // consumer의 exit 콜백 실행
      if (lifecycleCallbacks && typeof lifecycleCallbacks.exit === 'function') {
        try {
          lifecycleCallbacks.exit(currentContext, error)
        } catch (e) {
          console.error('[consumeElement] Error in exit callback', e)
        }
      }

      // provider의 disconnected 콜백 실행
      if (currentProvider && currentProvider._lifecycleCallbacks &&
        typeof currentProvider._lifecycleCallbacks.disconnected === 'function') {
        try {
          currentProvider._lifecycleCallbacks.disconnected(consumer)
        } catch (e) {
          console.error('[consumeElement] Error in disconnected callback', e)
        }
      }

      // provider에서 consumer 제거
      if (currentProvider) {
        currentProvider[SYM_PROVIDER_REMOVE_CONSUMER](consumer)
      }

      currentProvider = null
      currentContext = null
    },

    // broadcast 이벤트 구독
    listen(eventNameOrHandlers, handler) {
      if (typeof eventNameOrHandlers === 'string') {
        const fullEventName = buildBroadcastEventName(normalizedAssert, eventNameOrHandlers)
        const wrappedHandler = (event) => {
          handler(event.detail)
        }
        selectedElement.addEventListener(fullEventName, wrappedHandler)
        listenEntries.push({ eventName: fullEventName, handler: wrappedHandler })
      } else if (typeof eventNameOrHandlers === 'object' && eventNameOrHandlers !== null) {
        for (const [eventName, h] of Object.entries(eventNameOrHandlers)) {
          if (typeof h === 'function') {
            const fullEventName = buildBroadcastEventName(normalizedAssert, eventName)
            const wrappedHandler = (event) => {
              h(event.detail)
            }
            selectedElement.addEventListener(fullEventName, wrappedHandler)
            listenEntries.push({ eventName: fullEventName, handler: wrappedHandler })
          }
        }
      }

      return this
    },

    // context 업데이트 수신 (provider가 호출)
    _patchContext(newContext) {
      if (!isConnected) return

      currentContext = { ...currentContext, ...newContext }

      if (lifecycleCallbacks && typeof lifecycleCallbacks.update === 'function') {
        try {
          lifecycleCallbacks.update(currentContext)
        } catch (e) {
          console.error('[consumeElement] Error in update callback', e)
        }
      }
    },

    // provider가 consumer에게 메시지 전송
    _send(eventName, data) {
      if (lifecycleCallbacks && typeof lifecycleCallbacks[eventName] === 'function') {
        try {
          lifecycleCallbacks[eventName](data)
        } catch (e) {
          console.error(`[consumeElement] Error in ${eventName} handler`, e)
        }
      }
    }
  }

  // lifecycle 콜백 초기화
  if (typeof initializeCallback === 'function') {
    lifecycleCallbacks = initializeCallback(consumer)
  }

  // 자동 연결
  consumer.connect()

  return consumer
}

/**
 * assert 정규화
 */
function normalizeConsumerAssert(assert) {
  if (typeof assert === 'string') {
    return { provider: assert }
  }

  if (Array.isArray(assert)) {
    const result = { provider: assert[0] }
    if (assert[1]) result.consumer = assert[1]
    if (assert[2]) result.reference = assert[2]
    return result
  }

  if (typeof assert === 'object' && assert !== null) {
    return { ...assert }
  }

  throw new Error('[consumeElement] assert must be string, array, or object')
}

/**
 * provider-consumer 매칭 검사
 */
function matchProvider(collector, consumer, assert) {
  const matchers = collector.matchers

  for (const matcher of matchers) {
    if (matcher.type === 'name') {
      if (matcher.value === assert.provider) {
        return { matched: true }
      }
    } else if (matcher.type === 'function') {
      const result = matcher.value(consumer)
      if (result === true) {
        return { matched: true }
      }
      // false/undefined면 다음 matcher 시도 (연결 거부, 상위로 탐색 계속)
    }
  }

  return { matched: false }
}

/**
 * patchContext 헬퍼 (provider.consumers에서 사용)
 */
export function patchContext(consumer, newContext) {
  if (consumer && consumer._patchContext) {
    consumer._patchContext(newContext)
  }
}

/**
 * send 헬퍼 (provider.connected에서 사용)
 */
export function sendToConsumer(consumer, eventName, data) {
  if (consumer && consumer._send) {
    consumer._send(eventName, data)
  }
}
